import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import {
  Shield, LogIn, LogOut, AlertTriangle, Activity,
  Filter, RefreshCw, Users, Calendar, ChevronDown, ChevronUp, Eye
} from 'lucide-react';

const ROLE_COLORS = {
  admin:  'bg-purple-100 text-purple-700',
  worker: 'bg-blue-100 text-blue-700',
  client: 'bg-green-100 text-green-700',
};

const ACTION_COLORS = {
  LOGIN:        'bg-emerald-100 text-emerald-700',
  LOGOUT:       'bg-slate-100 text-slate-600',
  LOGIN_FAILED: 'bg-red-100 text-red-700',
  CREATE:       'bg-blue-100 text-blue-700',
  UPDATE:       'bg-amber-100 text-amber-700',
  DELETE:       'bg-red-100 text-red-700',
  VIEW:         'bg-gray-100 text-gray-600',
};

const ACTION_ICONS = {
  LOGIN:        <LogIn className="w-3.5 h-3.5" />,
  LOGOUT:       <LogOut className="w-3.5 h-3.5" />,
  LOGIN_FAILED: <AlertTriangle className="w-3.5 h-3.5" />,
  CREATE:       <Activity className="w-3.5 h-3.5" />,
  UPDATE:       <Activity className="w-3.5 h-3.5" />,
  DELETE:       <Activity className="w-3.5 h-3.5" />,
};

function Badge({ label, color }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {label}
    </span>
  );
}

function formatDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
      </div>
    </div>
  );
}


// ── Smart Diff Component ─────────────────────────────────────────────────────
function safeJson(val) {
  if (!val) return {};
  try { return typeof val === 'string' ? JSON.parse(val) : val; }
  catch { return {}; }
}

const FIELD_LABELS = {
  // Task
  status: 'Status', task_title: 'Task Title', task_description: 'Description',
  quantity_assigned: 'Qty Assigned', quantity_completed: 'Qty Completed',
  priority: 'Priority', due_date: 'Due Date', start_date: 'Start Date',
  worker_notes: 'Worker Notes', admin_notes: 'Admin Notes',
  assign_type: 'Assigned To', worker_id: 'Worker', department_id: 'Department',
  // Project
  name: 'Name', description: 'Description', client_name: 'Client',
  client_phone: 'Client Phone', order_date: 'Order Date', deadline: 'Deadline',
  total_amount: 'Total Amount', is_ready: 'Ready to Start',
  // Worker / User
  username: 'Username', phone: 'Phone', is_active: 'Active Status',
  rate_per_hour: 'Rate/Hour', department: 'Department',
  // Sale Challan / PO
  amount: 'Amount', payment_status: 'Payment Status',
  challan_date: 'Challan Date', po_number: 'PO Number',
  // General
  notes: 'Notes', remarks: 'Remarks', title: 'Title',
  company_name: 'Company', address: 'Address', unit: 'Unit',
  quantity: 'Quantity', material: 'Material', dimensions: 'Dimensions',
  unit_price: 'Unit Price', item_name: 'Item Name', proto_code: 'Proto Code',
};

const STATUS_LABELS = {
  active: '🟢 Active', inactive: '⚪ Inactive', completed: '✅ Completed',
  pending: '🔵 Pending', in_progress: '🟡 In Progress', on_hold: '🟠 On Hold',
  cancelled: '🔴 Cancelled', deleted: '🗑️ Deleted', paid: '✅ Paid',
  unpaid: '❌ Unpaid', partial: '🟡 Partial',
};

const SKIP_KEYS = ['id','created_at','updated_at','password','image_path','token','thumbnail_path','thumbnail_type'];

function formatVal(val, key) {
  if (val === null || val === undefined || val === '') return <span className="italic text-gray-300 text-[10px]">empty</span>;
  if (typeof val === 'boolean') return val ? '✅ Yes' : '❌ No';
  if ((key === 'is_active' || key === 'is_ready') && (val === 0 || val === 1 || val === '0' || val === '1'))
    return Number(val) === 1 ? '✅ Yes' : '❌ No';
  // Status human label
  if (key === 'status' || key === 'payment_status') return STATUS_LABELS[val] || val;
  // Date fields
  if ((key?.includes('date') || key?.includes('_at')) && String(val).includes('-')) {
    try {
      const d = new Date(val);
      if (!isNaN(d)) return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
    } catch {}
  }
  // Currency
  if (key?.includes('amount') || key?.includes('price') || key === 'rate_per_hour')
    return `₹${Number(val).toLocaleString('en-IN')}`;
  // Priority
  const PRIORITY = { low:'🟢 Low', medium:'🟡 Medium', high:'🟠 High', urgent:'🔴 Urgent' };
  if (key === 'priority') return PRIORITY[val] || val;
  return String(val);
}

function ChangedFields({ log }) {
  const oldObj = safeJson(log.old_value);
  const newObj = safeJson(log.new_value);

  // CREATE — show all new field values
  if (log.action_type === 'CREATE') {
    const entries = Object.entries(newObj).filter(([k, v]) =>
      !SKIP_KEYS.includes(k) && v !== null && v !== '' && v !== undefined
    );
    if (!entries.length) return <p className="text-xs text-gray-400">New record created</p>;
    return (
      <div>
        <p className="text-xs font-semibold text-blue-600 mb-2 flex items-center gap-1">✨ New Record Created</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {entries.map(([k, v]) => (
            <div key={k} className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wide">{FIELD_LABELS[k] || k.replace(/_/g,' ')}</p>
              <p className="text-xs text-gray-800 font-medium mt-0.5 break-words">{formatVal(v, k)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // DELETE
  if (log.action_type === 'DELETE') {
    const entries = Object.entries(oldObj).filter(([k, v]) =>
      !SKIP_KEYS.includes(k) && v !== null && v !== ''
    ).slice(0, 6);
    return (
      <div>
        <p className="text-xs font-semibold text-red-500 mb-2">🗑️ Record Deleted</p>
        {entries.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {entries.map(([k, v]) => (
              <div key={k} className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 opacity-70">
                <p className="text-[10px] text-red-400 font-bold uppercase tracking-wide">{FIELD_LABELS[k] || k.replace(/_/g,' ')}</p>
                <p className="text-xs text-gray-600 mt-0.5 line-through break-words">{formatVal(v, k)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Helper: check if a value is meaningfully empty
  const isEmpty = (v) => v === null || v === undefined || v === '' || v === 0 && false;
  const isObject = (v) => v !== null && typeof v === 'object';

  // UPDATE — show only fields truly changed by the user
  const allKeys = [...new Set([...Object.keys(oldObj), ...Object.keys(newObj)])]
    .filter(k => !SKIP_KEYS.includes(k));

  const changedKeys = allKeys.filter(k => {
    const o = oldObj[k];
    const n = newObj[k];
    // Skip if either side is a nested object/array (e.g. [object Object])
    if (isObject(o) || isObject(n)) return false;
    // Skip if both are null/empty
    const oEmpty = (o === null || o === undefined || o === '');
    const nEmpty = (n === null || n === undefined || n === '');
    if (oEmpty && nEmpty) return false;
    // Skip if value didn't meaningfully change
    return String(o ?? '') !== String(n ?? '');
  });

  if (!changedKeys.length)
    return <p className="text-xs text-gray-400 italic">No field changes detected</p>;

  return (
    <div>
      <p className="text-xs font-semibold text-amber-600 mb-3">
        ✏️ {changedKeys.length} field{changedKeys.length > 1 ? 's' : ''} changed
      </p>
      <div className="space-y-2">
        {changedKeys.map(k => {
          const oldVal = oldObj[k];
          const newVal = newObj[k];
          const oldEmpty = oldVal === null || oldVal === undefined || oldVal === '';
          const newEmpty = newVal === null || newVal === undefined || newVal === '';
          return (
            <div key={k} className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex items-start gap-3">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide w-28 flex-shrink-0 pt-0.5">
                {FIELD_LABELS[k] || k.replace(/_/g,' ')}
              </span>
              <div className="flex items-center gap-2 flex-wrap flex-1">
                {!oldEmpty && (
                  <span className="bg-red-50 border border-red-100 text-red-600 px-2.5 py-1 rounded-lg text-xs line-through">
                    {formatVal(oldVal, k)}
                  </span>
                )}
                {!oldEmpty && !newEmpty && (
                  <span className="text-gray-300 text-sm font-bold">→</span>
                )}
                {!newEmpty ? (
                  <span className="bg-green-50 border border-green-200 text-green-700 px-2.5 py-1 rounded-lg text-xs font-semibold">
                    {formatVal(newVal, k)}
                  </span>
                ) : (
                  <span className="bg-gray-50 border border-gray-200 text-gray-400 px-2.5 py-1 rounded-lg text-xs italic">
                    cleared
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AuditLogPage() {
  const [tab, setTab] = useState('login'); // 'login' | 'activity'
  const [loginLogs, setLoginLogs] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filters
  // Today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    role: '', action: '', action_type: '', module_name: '',
    date_from: today, date_to: today, limit: '100',
  });

  const loadSummary = useCallback(async () => {
    try { const r = await api.get('/audit/summary'); setSummary(r.data); } catch {}
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'login') {
        const r = await api.get('/audit/login-logs', { params: { role: filters.role, action: filters.action, date_from: filters.date_from, date_to: filters.date_to, limit: filters.limit } });
        setLoginLogs(r.data);
      } else {
        const r = await api.get('/audit/activity-logs', { params: { role: filters.role, action_type: filters.action_type, module_name: filters.module_name, date_from: filters.date_from, date_to: filters.date_to, limit: filters.limit } });
        setActivityLogs(r.data);
      }
    } catch {} finally { setLoading(false); }
  }, [tab, filters]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const modules = ['Task', 'Project', 'Worker', 'Department', 'Client', 'Material', 'Query', 'PackingBox', 'SaleChallan', 'PurchaseOrder'];

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
          <Shield className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Audit Trail</h1>
          <p className="text-xs text-gray-500">Full activity history — who changed what, when</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <SummaryCard icon={<LogIn className="w-5 h-5 text-emerald-600" />} label="Logins Today" value={summary.logins_today} color="bg-emerald-50" />
          <SummaryCard icon={<AlertTriangle className="w-5 h-5 text-red-500" />} label="Failed Today" value={summary.failed_today} color="bg-red-50" />
          <SummaryCard icon={<Activity className="w-5 h-5 text-blue-600" />} label="Activities Today" value={summary.activity_today} color="bg-blue-50" />
        </div>
      )}

      {/* Recently Active Users */}
      {summary?.recent_active_users?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Recently Active (Last 7 Days)</p>
          <div className="flex flex-wrap gap-2">
            {summary.recent_active_users.map((u, i) => (
              <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-200">
                <div className="w-6 h-6 bg-amber-500 rounded-md flex items-center justify-center text-white text-xs font-bold">{u.user_name?.charAt(0)}</div>
                <span className="text-xs font-medium text-gray-800">{u.user_name}</span>
                <Badge label={u.user_role} color={ROLE_COLORS[u.user_role] || 'bg-gray-100 text-gray-600'} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('login')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          Login Logs
        </button>
        <button onClick={() => setTab('activity')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'activity' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          Activity Logs
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Filters</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">

          {/* Role */}
          <select value={filters.role} onChange={e => setFilter('role', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-amber-400 outline-none">
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="worker">Worker</option>
            <option value="client">Client</option>
          </select>

          {/* Action — login tab */}
          {tab === 'login' && (
            <select value={filters.action} onChange={e => setFilter('action', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-amber-400 outline-none">
              <option value="">All Actions</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="LOGIN_FAILED">Failed</option>
            </select>
          )}

          {/* Action Type — activity tab */}
          {tab === 'activity' && (
            <select value={filters.action_type} onChange={e => setFilter('action_type', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-amber-400 outline-none">
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
            </select>
          )}

          {/* Module — activity tab */}
          {tab === 'activity' && (
            <select value={filters.module_name} onChange={e => setFilter('module_name', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-amber-400 outline-none">
              <option value="">All Modules</option>
              {modules.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}

          {/* Date From */}
          <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-amber-400 outline-none" />

          {/* Date To */}
          <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-amber-400 outline-none" />

          {/* Limit */}
          <select value={filters.limit} onChange={e => setFilter('limit', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-amber-400 outline-none">
            <option value="50">Last 50</option>
            <option value="100">Last 100</option>
            <option value="200">Last 200</option>
            <option value="500">Last 500</option>
          </select>

          <button onClick={loadLogs} className="flex items-center justify-center gap-1.5 bg-slate-900 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-slate-800 transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading...
            </div>
          ) : tab === 'login' ? (
            /* ── LOGIN LOGS TABLE ── */
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'User', 'Role', 'Action', 'IP Address', 'Date & Time'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loginLogs.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400">No login logs found</td></tr>
                ) : loginLogs.map((log, i) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-amber-500 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {(log.user_name || '?').charAt(0)}
                        </div>
                        <span className="font-medium text-gray-900">{log.user_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><Badge label={log.user_role} color={ROLE_COLORS[log.user_role] || 'bg-gray-100 text-gray-600'} /></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[log.action] || 'bg-gray-100'}`}>
                        {ACTION_ICONS[log.action]} {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{log.ip_address || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(log.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            /* ── ACTIVITY LOGS TABLE ── */
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'User', 'Role', 'Action', 'Module', 'Description', 'Date & Time', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activityLogs.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-400">No activity logs found</td></tr>
                ) : activityLogs.map((log, i) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-amber-500 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(log.user_name || '?').charAt(0)}
                          </div>
                          <span className="font-medium text-gray-900">{log.user_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><Badge label={log.user_role} color={ROLE_COLORS[log.user_role] || 'bg-gray-100 text-gray-600'} /></td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[log.action_type] || 'bg-gray-100'}`}>
                          {ACTION_ICONS[log.action_type]} {log.action_type}
                        </span>
                      </td>
                      <td className="px-4 py-3"><Badge label={log.module_name} color="bg-slate-100 text-slate-700" /></td>
                      <td className="px-4 py-3 text-gray-600 text-xs max-w-xs truncate">{log.description}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3">
                        {(log.old_value || log.new_value) && (
                          <button onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600">
                            {expandedRow === log.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Always show changes for UPDATE; toggle for CREATE/DELETE */}
                    {(expandedRow === log.id || log.action_type === 'UPDATE') && (log.old_value || log.new_value) && (
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <td colSpan={8} className="px-6 py-4">
                          <ChangedFields log={log} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {/* Row count */}
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
          Showing {tab === 'login' ? loginLogs.length : activityLogs.length} records
        </div>
      </div>
    </div>
  );
}
